//src/hooks/useUserProfile.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearProfileCache,
  getProfileChangedEventName,
  isBrowser,
  isProfileCacheStale,
  normalizeUserProfile,
  PROFILE_CACHE_TTL,
  readProfileCache,
  writeProfileCache,
} from '../utils/profile';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const getProfileEndpoint = () => {
    const base = API_BASE_URL.replace(/\/$/, '');
    return base ? `${base}/profile` : '/profile';
};

export const useUserProfile = ({ enabled = true } = {}) => {
    const initialCache = useMemo(() => readProfileCache(), []);
    const initialProfile = initialCache?.data ?? null;

    const [profile, setProfile] = useState(initialProfile);
    const [loading, setLoading] = useState(!initialProfile);
    const [isFetching, setIsFetching] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(initialCache?.cachedAt ?? null);
    const [isStale, setIsStale] = useState(
        Boolean(initialCache?.cachedAt && isProfileCacheStale(initialCache.cachedAt))
    );

    const profileRef = useRef(initialProfile);
    const abortRef = useRef(null);
    const mountedRef = useRef(false);

    useEffect(() => {
        profileRef.current = profile;
    }, [profile]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    const syncFromCache = useCallback(() => {
        const cached = readProfileCache();

        if (cached?.data) {
            setProfile(cached.data);
            profileRef.current = cached.data;
            setLastUpdated(cached.cachedAt);
            setIsStale(isProfileCacheStale(cached.cachedAt));
            return cached.data;
        }

        return null;
    }, []);

    const fetchProfile = useCallback(
        async ({ silent = false } = {}) => {
            if (!enabled || !isBrowser()) return null;

            const token = window.localStorage.getItem('mp_token');

            if (!token) {
                clearProfileCache();
                setProfile(null);
                profileRef.current = null;
                setError(null);
                setLoading(false);
                setIsFetching(false);
                setLastUpdated(null);
                setIsStale(false);
                return null;
            }

            abortRef.current?.abort();

            const controller = new AbortController();
            abortRef.current = controller;

            const hasCachedProfile = Boolean(profileRef.current);
            const showLoading = !silent && !hasCachedProfile;

            if (showLoading) {
                setLoading(true);
            }

            setIsFetching(true);
            if (!silent) {
                setError(null);
            }

            try {
                const res = await fetch(getProfileEndpoint(), {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    signal: controller.signal,
                });

                const json = await res.json().catch(() => null);

                if (!res.ok || !json?.success) {
                    throw new Error(json?.message || 'Gagal memuat profile');
                }

                const nextProfile = normalizeUserProfile(json.data);

                if (!mountedRef.current) {
                    return nextProfile;
                }

                setProfile(nextProfile);
                profileRef.current = nextProfile;
                setLastUpdated(Date.now());
                setIsStale(false);
                setError(null);

                writeProfileCache(nextProfile);

                return nextProfile;
            } catch (err) {
                if (err?.name !== 'AbortError' && mountedRef.current) {
                    if (!silent || !profileRef.current) {
                        setError(err?.message || 'Terjadi kesalahan saat memuat profile');
                    }
                    setIsStale(Boolean(profileRef.current));
                }

                return profileRef.current;
            } finally {
                if (mountedRef.current) {
                    if (showLoading) setLoading(false);
                    setIsFetching(false);
                }
            }
        },
        [enabled]
    );

    const refresh = useCallback(() => {
        return fetchProfile({ silent: true });
    }, [fetchProfile]);

    const clearProfile = useCallback(() => {
        clearProfileCache();
        setProfile(null);
        profileRef.current = null;
        setLastUpdated(null);
        setIsStale(false);
        setError(null);
        setLoading(false);
        setIsFetching(false);
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const hasCachedProfile = Boolean(initialProfile);

        if (hasCachedProfile) {
            setLoading(false);
            setIsStale(Boolean(initialCache?.cachedAt && isProfileCacheStale(initialCache.cachedAt)));
        } else {
            setLoading(true);
        }

        fetchProfile({
            silent: hasCachedProfile,
        });
    }, [enabled, fetchProfile, initialCache?.cachedAt, initialProfile]);

    useEffect(() => {
        if (!enabled || !isBrowser()) return undefined;

        const handleFocus = () => {
            fetchProfile({ silent: true });
        };

        const handleOnline = () => {
            fetchProfile({ silent: true });
        };

        const handleStorage = (event) => {
            if (event.key === 'mp_profile_cache') {
                const cached = syncFromCache();

                if (!cached && !window.localStorage.getItem('mp_token')) {
                    clearProfile();
                }
            }
        };

        const handleProfileChanged = () => {
            syncFromCache();
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('online', handleOnline);
        window.addEventListener('storage', handleStorage);
        window.addEventListener(getProfileChangedEventName(), handleProfileChanged);

        const interval = window.setInterval(() => {
            fetchProfile({ silent: true });
        }, PROFILE_CACHE_TTL);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener(getProfileChangedEventName(), handleProfileChanged);
            window.clearInterval(interval);
        };
    }, [clearProfile, enabled, fetchProfile, syncFromCache]);

    const subscription = profile?.subscription ?? null;

    const quota = useMemo(() => {
        return subscription?.quota?.properties ?? null;
    }, [subscription]);

    const avatarUrl = useMemo(() => {
        return profile?.avatar_url || '';
    }, [profile]);

    const displayName = useMemo(() => {
        return profile?.display_name || 'User';
    }, [profile]);

    const isSubscribed = Boolean(subscription?.id);
    const isSubscriptionActive = Boolean(subscription?.is_active);

    return {
        profile,
        subscription,
        quota,
        avatarUrl,
        displayName,
        loading,
        isFetching,
        error,
        lastUpdated,
        isStale,
        isSubscribed,
        isSubscriptionActive,
        refresh,
        clearProfile,
    };
};
